import { Component } from '@theme/component';
import { evaluate, fromMetaobjects, toCatalogFacts } from '@theme/rig-engine';

const SAVED_KEY = 'sc-configurator-saved';
/**
 * The rig this browser last added to the cart: its merged line's key and original line price.
 *
 * Editing a build and checking out again used to append a second rig rather than replace the
 * first, so a customer who changed their mind ended up buying two. The Cart Transform merges the
 * components into one parent line, and that line is what has to go on the next checkout.
 *
 * The key alone cannot identify it. Shopify derives a merged line's key from the bundle parent
 * variant and the attributes the Cart Transform stamps, not from the components, so two rigs with
 * the same wheelbase and wheel share one key however much else differs. Removing "by key" then
 * hits line 1, which is the NEWEST rig. The price tells two same-key rigs apart, and the removal
 * itself goes by line number, which is unambiguous. See `replacementPlan`.
 */
const CART_LINE_KEY = 'sc-configurator-cart-line';

/** The last rig this browser emailed via "Rig saved", so saving the same rig twice sends one email. */
const EMAILED_KEY = 'sc-configurator-emailed';

/**
 * Every option this browser has seen, as handle -> [step role, market-neutral name].
 *
 * Each market build can carry its own copy of an option (the UK wheelbase is `sc3-sport-uk`, the
 * EU one `sc3-sport`), and the build lives in the URL by handle. Switching market therefore
 * reloaded a URL whose wheelbase the new build did not know, and the part silently fell out, taking
 * the bundle saving with it. Recording each handle's role and name lets the next build find its own
 * copy of the same part.
 */
const PORTABLE_KEY = 'sc-configurator-portable';

/**
 * A part's name with the market and brand noise taken out, so "Simucube 3 Sport" and a UK copy
 * titled "Simucube 3 Sport UK" compare equal. The step's role in brackets goes too: the match is
 * always made within one role.
 */
function portableName(title) {
  return String(title ?? '')
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\b(simucube|uk|us|eu)\b/g, ' ')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * What to do to the cart after a rig was added, given the lines before and after the add.
 *
 * Shopify puts new lines at the top of the cart, so the lines that were there before keep their
 * order behind the new ones. That is what lets the previous rig be found by position. An identical
 * rig does not become a new line at all: Shopify merges it into the existing one at quantity 2,
 * and only a removal brings that back down, since it ignores a quantity set on a merged line.
 *
 * Returns the line to remember for next time and, when the previous rig is still in the cart, the
 * `cart/change.js` call that takes it out. Pure, so the rule can be read in one place.
 */
function replacementPlan(previous, before, after) {
  if (!Array.isArray(before) || !Array.isArray(after)) return { remember: null, change: null };
  const fresh = after.length - before.length;

  const indexOfPrevious = () => {
    if (!previous) return -1;
    const exact = before.findIndex((line) => line.key === previous.key && line.price === previous.price);
    return exact !== -1 ? exact : before.findIndex((line) => line.key === previous.key);
  };

  if (fresh > 0) {
    const remember = { key: after[0].key, price: after[0].price };
    const index = indexOfPrevious();
    return { remember, change: index === -1 ? null : { line: index + fresh + 1, quantity: 0 } };
  }

  if (fresh === 0) {
    // The rig merged into an existing identical line. Find it by the quantity that grew.
    const index = after.findIndex((line, i) => line.quantity > (before[i]?.quantity ?? 0));
    if (index === -1) return { remember: null, change: null };
    const previousIndex = indexOfPrevious();
    if (previousIndex === index) {
      // Re-added the rig already in the cart: one rig was wanted, not two. Shopify answers a
      // quantity set on a merged line with 200 and changes nothing, so the quantity cannot simply
      // be put back. A removal is the one change it honours, and on a line holding two merged
      // rigs it takes out one rig, not the line (verified against the live cart, 2026-09-21).
      // The line is back to its old price once that lands, so that is the price to remember.
      const remember = { key: before[index].key, price: before[index].price };
      return { remember, change: { line: index + 1, quantity: 0 } };
    }
    const remember = { key: after[index].key, price: after[index].price };
    return { remember, change: previousIndex === -1 ? null : { line: previousIndex + 1, quantity: 0 } };
  }

  return { remember: null, change: null };
}
const SYSTEM_STEP = 'system-components';
const ACCORDION_OPEN_DURATION = 1000;
const ACCORDION_CLOSE_DURATION = 700;
const ACCORDION_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
const WALL_LAYER_ORDER = 7;

function formatMoney(cents, format) {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const placeholder = format.match(placeholderRegex)?.[1] ?? 'amount';

  const formatWithDelimiters = (number, precision, thousands, decimal) => {
    const [whole = '0', fraction] = (number / 100).toFixed(precision).split('.');
    const wholeWithThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? wholeWithThousands + decimal + fraction : wholeWithThousands;
  };

  let value;
  switch (placeholder) {
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = formatWithDelimiters(cents, 2, ',', '.');
  }

  return format.replace(placeholderRegex, value);
}

class SimucubeConfigurator extends Component {
  requiredRefs = ['bootstrap', 'panel'];

  #abortController = new AbortController();

  #config;

  #catalog = {};

  #options = new Map();

  #optionIdByHandle = new Map();

  #moduleHandleByOptionId = new Map();

  #modulesById = new Map();

  #modulesByHandle = new Map();

  #systemModules = new Set();

  #selection = new Map();

  #selectionSeq = new Map();

  #selectionCounter = 0;

  #skips = new Set();

  #autoAdded = new Set();

  #dismissed = new Map();

  #fallbackItems = new Map();

  #fallbackSelection = new Set();

  #quantities = new Map();

  #accordionAnimations = new WeakMap();

  #suppressedProducts = new Set();

  #result = { suggestions: [], suppressed: [], blocking: [], notices: [] };

  #historyTimer;

  #booted = false;

  #saved = false;

  #saveLabelText;

  #adoptSystemSelections = false;

  connectedCallback() {
    super.connectedCallback();

    if (!this.#boot()) return;

    const { signal } = this.#abortController;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('sc-configurator:step', this.#onStepChange, { signal });
    window.addEventListener('popstate', this.#onPopState, { signal });
    window.addEventListener('resize', this.#fitBarActions, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });

    this.#saved = this.#restore();
    this.#update({ history: 'replace' });
    this.#booted = true;

    this.#preloadStep(this.querySelector('.sc-cfg-step')?.dataset.step ?? '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #onPageShow = (event) => {
    if (!event.persisted) return;
    this.refs.checkoutButton?.removeAttribute('aria-busy');
    this.#update({ history: 'replace' });
  };

  #boot() {
    let blob;

    try {
      blob = JSON.parse(this.refs.bootstrap.textContent ?? '');
    } catch {
      return false;
    }

    if (!blob?.configurator) return false;

    this.#systemModules = new Set((this.dataset.systemModules ?? '').split(',').filter(Boolean));

    const rawStore = {
      configurators: [this.#toRawNode(blob.configurator)],
      modules: (blob.modules ?? []).map((node) => this.#toRawNode(node)),
      options: (blob.options ?? []).map((node) => this.#toRawNode(node)),
      rules: (blob.rules ?? []).map((node) => this.#toRawNode(node)),
    };

    this.#config = fromMetaobjects(rawStore)[0];
    if (!this.#config) return false;

    this.#catalog = this.#toCatalog(blob.catalog);

    for (const node of blob.modules ?? []) {
      this.#modulesById.set(node.id, node);
      this.#modulesByHandle.set(node.handle, node);
      for (const optionId of node.fields?.options ?? []) {
        this.#moduleHandleByOptionId.set(optionId, node.handle);
      }
    }

    for (const node of blob.options ?? []) {
      this.#options.set(node.id, node);
      this.#optionIdByHandle.set(node.handle, node.id);
    }
    this.#rememberPortable();

    for (const card of this.querySelectorAll('[data-fallback-id]')) {
      if (!(card instanceof HTMLElement)) continue;
      const variantId = card.dataset.fallbackId ?? '';
      if (!variantId) continue;
      this.#fallbackItems.set(variantId, {
        variantId,
        moduleHandle: card.dataset.module ?? '',
        price: Number(card.dataset.price ?? 0),
        title: card.querySelector('.sc-cfg-card__title, .sc-cfg-acc-card__title')?.textContent?.trim() ?? '',
        thumbnail: card.querySelector('img')?.getAttribute('src') ?? '',
        // Liquid marks a sold-out fallback card with this class; read it rather than a second
        // data attribute so the card markup stays the single source of truth.
        available: !card.classList.contains('sc-cfg-card--unavailable'),
      });
    }

    return true;
  }

  #toRawNode(entity) {
    const fields = [];

    for (const [key, value] of Object.entries(entity.fields ?? {})) {
      if (value === null || value === undefined || value === '') continue;
      fields.push({ key, value: typeof value === 'string' ? value : JSON.stringify(value) });
    }

    return { id: entity.id, handle: entity.handle, displayName: entity.handle, fields };
  }

  #toCatalog(raw) {
    const catalog = {};

    for (const [id, product] of Object.entries(raw ?? {})) {
      catalog[id] = {
        ...toCatalogFacts(product),
        id,
        title: product.title ?? '',
        active: true,
      };
    }

    return catalog;
  }

  #stepHandle(moduleHandle) {
    return this.#systemModules.has(moduleHandle) ? SYSTEM_STEP : moduleHandle;
  }

  #stepTitle(moduleHandle) {
    return this.#modulesByHandle.get(moduleHandle)?.fields?.title ?? '';
  }

  #selectedOptionIds() {
    const ids = [];
    for (const set of this.#selection.values()) ids.push(...set);
    return ids;
  }

  #firstSelected(moduleHandle) {
    let firstId = '';
    let firstSeq = Infinity;

    for (const optionId of this.#selection.get(moduleHandle) ?? []) {
      const seq = this.#selectionSeq.get(optionId) ?? 0;
      if (seq >= firstSeq) continue;
      firstSeq = seq;
      firstId = optionId;
    }

    return firstId;
  }

  #isSelected(optionId) {
    const moduleHandle = this.#moduleHandleByOptionId.get(optionId);
    if (!moduleHandle) return false;
    return this.#selection.get(moduleHandle)?.has(optionId) === true;
  }

  /**
   * Availability comes from `display.available`, which Liquid renders from the variant. The card is
   * already disabled server-side; this guards every path that does not go through a click — a
   * shared URL, a restored session, an auto-add rule.
   */
  #isAvailable(optionId) {
    const display = this.#options.get(optionId)?.display;
    if (!display) return true;
    return display.available !== false;
  }

  /**
   * Reveal the step's shared notify form and point it at the part the customer actually clicked.
   * The subject line is what reaches the merchant's inbox, so it names the variant, not just the
   * product — "sold out" on a rig is usually one variant of several.
   */
  #openNotifyForm(button) {
    const step = button.closest('.sc-cfg-step');
    const host = step?.querySelector('[data-notify-form-host]');
    if (!(host instanceof HTMLElement)) return;

    const title = button.dataset.notifyTitle ?? '';
    const variantId = button.dataset.notifyVariant ?? '';

    const subject = host.querySelector('[data-notify-subject]');
    if (subject instanceof HTMLInputElement) {
      subject.value = variantId ? `${title} (variant ${variantId})` : title;
    }

    const label = host.querySelector('[data-notify-label]');
    if (label instanceof HTMLElement && title) {
      // Cache the authored copy on first use, so clicking a second card replaces the part name
      // instead of appending to it.
      label.dataset.template ??= label.textContent.trim();
      label.textContent = `${label.dataset.template}: ${title}`;
    }

    host.hidden = false;
    host.querySelector('input[type="email"]')?.focus();
  }

  /** A required step is unsatisfiable when it has options and every one of them is sold out. */
  #stepBlockedByStock(stepHandle) {
    let considered = 0;

    for (const [optionId, moduleHandle] of this.#moduleHandleByOptionId) {
      if (this.#stepHandle(moduleHandle) !== stepHandle) continue;
      considered += 1;
      if (this.#isAvailable(optionId)) return false;
    }

    for (const item of this.#fallbackItems.values()) {
      if (this.#stepHandle(item.moduleHandle) !== stepHandle) continue;
      considered += 1;
      if (item.available !== false) return false;
    }

    return considered > 0;
  }

  #addSelection(optionId) {
    const moduleHandle = this.#moduleHandleByOptionId.get(optionId);
    if (!moduleHandle) return false;
    // Never let a sold-out option into the selection. It would price into the running subtotal and
    // then fail at add-to-cart, after the customer has built the whole rig.
    if (!this.#isAvailable(optionId)) return false;

    const module = this.#modulesByHandle.get(moduleHandle);
    const multiSelect = module?.fields?.multi_select === true;
    const current = this.#selection.get(moduleHandle) ?? new Set();

    if (current.has(optionId)) return false;

    if (!multiSelect) {
      for (const previous of current) this.#autoAdded.delete(previous);
      current.clear();
    }

    current.add(optionId);
    this.#selection.set(moduleHandle, current);
    this.#selectionSeq.set(optionId, (this.#selectionCounter += 1));
    this.#skips.delete(moduleHandle);
    return true;
  }

  #removeSelection(optionId) {
    const moduleHandle = this.#moduleHandleByOptionId.get(optionId);
    if (!moduleHandle) return false;

    const current = this.#selection.get(moduleHandle);
    if (!current?.has(optionId)) return false;

    current.delete(optionId);
    this.#selectionSeq.delete(optionId);
    return true;
  }

  #triggerSignature() {
    return this.#selectedOptionIds()
      .filter((id) => !this.#systemModules.has(this.#moduleHandleByOptionId.get(id) ?? ''))
      .sort()
      .join(',');
  }

  #systemOptionForProduct(productId) {
    for (const option of this.#options.values()) {
      if (option.fields?.product !== productId) continue;
      if (!this.#systemModules.has(this.#moduleHandleByOptionId.get(option.id) ?? '')) continue;
      return option;
    }
    return undefined;
  }

  #runRules() {
    let result = evaluate(
      { selectedOptionIds: this.#selectedOptionIds(), quantities: Object.fromEntries(this.#quantities) },
      this.#config,
      this.#catalog,
    );

    for (let pass = 0; pass < 2; pass += 1) {
      const suppressed = new Set(result.suppressed.map((item) => item.productId));
      let changed = false;

      for (const option of this.#options.values()) {
        if (!suppressed.has(option.fields?.product)) continue;
        if (this.#removeSelection(option.id)) {
          this.#autoAdded.delete(option.id);
          changed = true;
        }
      }

      const signature = this.#triggerSignature();
      for (const [productId, storedSignature] of this.#dismissed) {
        if (storedSignature !== signature) this.#dismissed.delete(productId);
      }

      // A part the builder added on its own goes again once nothing asks for it. Removing the SC3
      // or the Savu that brought the Link Hub in left the hub ticked and priced, for a rig that no
      // longer needs one. Only parts the builder added are touched: one the customer ticked stays.
      const wanted = new Set(
        result.suggestions
          .filter((suggestion) => suggestion.defaultSelected && !suppressed.has(suggestion.productId))
          .map((suggestion) => suggestion.productId),
      );
      if (this.#adoptSystemSelections) {
        this.#adoptSystemSelections = false;
        for (const optionId of this.#selectedOptionIds()) {
          if (!this.#systemModules.has(this.#moduleHandleByOptionId.get(optionId) ?? '')) continue;
          if (wanted.has(this.#options.get(optionId)?.fields?.product)) this.#autoAdded.add(optionId);
        }
      }
      for (const optionId of [...this.#autoAdded]) {
        if (wanted.has(this.#options.get(optionId)?.fields?.product)) continue;
        this.#autoAdded.delete(optionId);
        if (this.#removeSelection(optionId)) changed = true;
      }

      for (const suggestion of result.suggestions) {
        if (!suggestion.defaultSelected) continue;
        if (suppressed.has(suggestion.productId)) continue;
        if (this.#dismissed.has(suggestion.productId)) continue;

        const target = this.#systemOptionForProduct(suggestion.productId);
        if (!target || this.#isSelected(target.id)) continue;

        if (this.#addSelection(target.id)) {
          this.#autoAdded.add(target.id);
          changed = true;
        }
      }

      if (!changed) break;
      result = evaluate(
        { selectedOptionIds: this.#selectedOptionIds(), quantities: Object.fromEntries(this.#quantities) },
        this.#config,
        this.#catalog,
      );
    }

    this.#result = result;
    this.#suppressedProducts = new Set(result.suppressed.map((item) => item.productId));
  }

  #qtyOf(key) {
    return this.#quantities.get(key) ?? 1;
  }

  /**
   * The stage layer this option contributes, sized for a cart thumbnail. Returns nothing for an
   * option that draws no layer at all (the Link Hub and Ethernet switch are list rows, not art),
   * so those never claim a slot in the composition.
   */
  #cartLayerFor(optionId, option) {
    const display = option.display ?? {};
    const moduleHandle = this.#moduleHandleByOptionId.get(optionId) ?? '';

    if (this.#firstSelected(moduleHandle) !== optionId) {
      if (!display.wallThumbUrl) return {};
      return {
        layer: display.wallThumbUrl,
        layerOrder: Number(display.wallLayerOrder ?? WALL_LAYER_ORDER),
      };
    }

    if (!display.previewThumbUrl) return {};
    return { layer: display.previewThumbUrl, layerOrder: Number(display.layerOrder ?? 1) };
  }

  #selectedLines() {
    const lines = [];

    for (const optionId of this.#selectedOptionIds()) {
      const option = this.#options.get(optionId);
      if (!option) continue;
      if (this.#suppressedProducts.has(option.fields?.product)) continue;

      lines.push({
        optionId,
        moduleHandle: this.#moduleHandleByOptionId.get(optionId) ?? '',
        title: option.fields?.title ?? option.display?.productTitle ?? '',
        variantId: option.display?.variantId,
        price: Number(option.display?.priceCents ?? 0),
        thumbnail: option.display?.thumbnailUrl ?? '',
        rigRender: option.display?.previewThumbUrl ?? '',
        // The layer this option contributes to the stage, so the cart can rebuild the same
        // composition. Mirrors #stageLayers exactly: in a multi-select step only the first
        // selection is mounted on the rig and the rest hang on the wall, so a three-wheel build
        // does not stack three wheels on the same hub.
        ...this.#cartLayerFor(optionId, option),
        autoAdded: this.#autoAdded.has(optionId),
        quantity: this.#qtyOf(optionId),
      });
    }

    for (const variantId of this.#fallbackSelection) {
      const item = this.#fallbackItems.get(variantId);
      if (!item) continue;
      lines.push({ ...item, autoAdded: false, quantity: this.#qtyOf(variantId) });
    }

    return lines;
  }

  #total() {
    return this.#selectedLines().reduce((sum, line) => sum + (Number(line.price) || 0) * (line.quantity ?? 1), 0);
  }

  #bundleSaving(lines) {
    const rate = Number(this.dataset.bundleDiscount) || 0;
    if (rate <= 0) return 0;

    const wheelbases = lines.filter((line) => line.moduleHandle === 'wheelbase');
    const wheels = lines.filter((line) => line.moduleHandle === 'steering-wheel');
    if (wheelbases.length === 0 || wheels.length === 0) return 0;

    return [...wheelbases, ...wheels].reduce(
      (sum, line) => sum + Math.round(((Number(line.price) || 0) * (line.quantity ?? 1) * rate) / 100),
      0
    );
  }

  #update(options = {}) {
    this.#runRules();
    this.#renderOptions();
    this.#renderSystemRows();
    this.#renderNotices();
    this.#renderTabs();
    this.#renderBar();
    this.#renderStage();

    const panel = this.refs.panel;
    if (panel instanceof HTMLElement && panel.dataset.view === 'summary') this.#renderSummary(false);

    this.#refreshRails();
    this.#persist(options.history ?? true);
    if (this.#saved) this.#store(this.#payload());
  }

  #refreshRails(root = this) {
    for (const rail of root.querySelectorAll('sc-config-rail')) {
      if (typeof rail.refresh === 'function') rail.refresh();
    }
  }

  #renderOptions() {
    for (const element of this.querySelectorAll('[data-option-id]')) {
      if (!(element instanceof HTMLElement)) continue;
      const optionId = element.dataset.optionId ?? '';
      const selected = this.#isSelected(optionId);

      if (element.classList.contains('sc-cfg-card') || element.classList.contains('sc-cfg-acc-card')) {
        element.dataset.selected = String(selected);
        element.querySelector('[data-select-option]')?.setAttribute('aria-pressed', String(selected));
      }
    }

    for (const element of this.querySelectorAll('[data-fallback-id]')) {
      if (!(element instanceof HTMLElement)) continue;
      const selected = this.#fallbackSelection.has(element.dataset.fallbackId ?? '');
      element.dataset.selected = String(selected);
      element.querySelector('[data-select-option]')?.setAttribute('aria-pressed', String(selected));
    }

    for (const value of this.querySelectorAll('[data-qty-value]')) {
      const host = value.closest('[data-option-id], [data-fallback-id]');
      if (!(host instanceof HTMLElement)) continue;
      const qty = this.#qtyOf(host.dataset.optionId || host.dataset.fallbackId || '');
      value.textContent = String(qty);
      host.dataset.qty = String(qty);
    }

    for (const group of this.querySelectorAll('[data-acc-group]')) {
      const pill = group.querySelector('[data-group-count]');
      if (!(pill instanceof HTMLElement)) continue;
      const count = group.querySelectorAll('[data-selected="true"]').length;
      const template = pill.dataset.selectedText ?? '[count] Selected';
      pill.hidden = count === 0;
      pill.textContent = template.replace('[count]', String(count));
    }
  }

  #renderSystemRows() {
    const suggestions = new Map(this.#result.suggestions.map((item) => [item.productId, item]));
    const suppressions = new Map(this.#result.suppressed.map((item) => [item.productId, item]));
    const rulesById = new Map((this.#config.rules ?? []).map((rule) => [rule.id, rule]));

    for (const row of this.querySelectorAll('.sc-cfg-sysrow')) {
      if (!(row instanceof HTMLElement)) continue;

      const optionId = row.dataset.optionId ?? '';
      const productId = row.dataset.product ?? '';
      const tag = row.querySelector('[data-row-tag]');
      const message = row.querySelector('[data-row-message]');
      const hit = row.querySelector('[data-select-option]');
      const suppression = suppressions.get(productId);
      const suggestion = suggestions.get(productId);

      let state = 'neutral';
      let tagText = '';
      let messageText = '';

      if (suppression) {
        state = 'included';
        tagText = this.dataset.includedLabel ?? '';
        messageText = rulesById.get(suppression.ruleId)?.message || this.dataset.includedFallback || '';
      } else if (suggestion) {
        state = 'recommended';
        tagText = this.dataset.recommendedLabel ?? '';
        messageText = suggestion.message || this.dataset.recommendedFallback || '';
      }

      row.dataset.state = state;
      row.dataset.selected = String(this.#isSelected(optionId));

      if (tag instanceof HTMLElement) {
        tag.hidden = !tagText;
        tag.textContent = tagText;
      }

      if (message instanceof HTMLElement) {
        message.hidden = !messageText;
        message.innerHTML = messageText;
      }

      if (hit instanceof HTMLElement) {
        hit.setAttribute('aria-checked', String(this.#isSelected(optionId)));
      }
    }
  }

  #renderNotices() {
    for (const container of this.querySelectorAll('[data-step-notices]')) {
      container.replaceChildren();
    }

    for (const notice of this.#result.notices) {
      if (!notice.message) continue;

      const module = notice.stepId ? this.#modulesById.get(notice.stepId) : undefined;
      const stepHandle = module ? this.#stepHandle(module.handle) : undefined;
      if (!stepHandle) continue;

      const container = this.querySelector(`.sc-cfg-step[data-step="${CSS.escape(stepHandle)}"] [data-step-notices]`);
      if (!container) continue;

      const template = this.refs.noticeTemplate;
      if (!(template instanceof HTMLTemplateElement)) continue;

      const strip = template.content.cloneNode(true);
      const text = strip.querySelector('[data-notice-message]');
      if (text) text.innerHTML = notice.message;
      container.append(strip);
    }
  }

  #renderTabs() {
    const panel = this.refs.panel;
    if (!(panel instanceof HTMLElement) || typeof panel.setTabComplete !== 'function') return;

    for (const step of this.querySelectorAll('.sc-cfg-step')) {
      if (!(step instanceof HTMLElement)) continue;
      const stepHandle = step.dataset.step ?? '';
      let complete = false;

      for (const [moduleHandle, ids] of this.#selection) {
        if (this.#stepHandle(moduleHandle) !== stepHandle) continue;
        if (ids.size > 0) complete = true;
      }

      for (const moduleHandle of this.#skips) {
        if (this.#stepHandle(moduleHandle) === stepHandle) complete = true;
      }

      for (const item of this.#fallbackItems.values()) {
        if (item.moduleHandle === stepHandle && this.#fallbackSelection.has(item.variantId)) complete = true;
      }

      // A required step with nothing buyable left cannot be completed by choosing. Mark the step so
      // its notice renders, and let "I already own this" stay the way past it — per the client's
      // "rules inform, never force" sign-off we surface the dead end, we do not trap the customer.
      const blocked = this.#stepBlockedByStock(stepHandle);
      step.toggleAttribute('data-stock-blocked', blocked);

      panel.setTabComplete(stepHandle, complete);
    }
  }

  #fitBarActions = () => {
    const { bar, barActions } = this.refs;
    if (!bar || !barActions) return;

    barActions.classList.remove('sc-cfg__bar-actions--stacked');
    barActions.classList.toggle('sc-cfg__bar-actions--stacked', bar.scrollWidth > bar.clientWidth);
    document.documentElement.style.setProperty('--sc-cfg-bar-offset', `${bar.offsetHeight}px`);
  };

  #renderBar() {
    const lines = this.#selectedLines();
    const format = this.dataset.moneyFormat ?? '{{ amount }}';

    const total = this.#total();
    const saving = this.#bundleSaving(lines);

    if (this.refs.total) this.refs.total.textContent = formatMoney(total - saving, format);
    if (this.refs.totalCompare) {
      this.refs.totalCompare.hidden = saving <= 0;
      this.refs.totalCompare.textContent = saving > 0 ? formatMoney(total, format) : '';
    }
    this.#fitBarActions();

    for (const toggle of this.querySelectorAll('[data-open-summary]')) {
      if (toggle instanceof HTMLButtonElement) toggle.disabled = lines.length === 0;
    }

    for (const host of this.querySelectorAll('[data-selections-template]')) {
      if (!(host instanceof HTMLElement)) continue;
      host.hidden = lines.length === 0;
      const label = host.querySelector('[data-selections-count]');
      if (label) label.textContent = host.dataset.selectionsTemplate.replace('[count]', String(lines.length));
    }

    const checkout = this.refs.checkoutButton;
    if (checkout) {
      const wasDisabled = checkout.disabled;
      checkout.disabled = lines.length === 0 || checkout.getAttribute('aria-busy') === 'true';
      if (wasDisabled && !checkout.disabled) {
        document.dispatchEvent(new CustomEvent('bubble-button:init', { detail: { root: this } }));
      }
    }

    const reset = this.refs.resetButton;
    if (reset instanceof HTMLButtonElement) reset.disabled = lines.length === 0 && this.#skips.size === 0;

    this.#renderSaveButton();
  }

  #renderSaveButton() {
    const button = this.refs.saveButton;
    if (button instanceof HTMLElement) {
      if (this.#saved) button.dataset.saved = 'true';
      else delete button.dataset.saved;
      button.setAttribute('aria-pressed', String(this.#saved));
    }

    const label = this.refs.saveLabel;
    if (!(label instanceof HTMLElement)) return;

    this.#saveLabelText ??= label.textContent ?? '';
    const savedLabel =
      (this.#savesToCart() ? this.dataset.savedToCartLabel : null) || this.dataset.savedLabel || this.#saveLabelText;
    label.textContent = this.#saved ? savedLabel : this.#saveLabelText;
  }

  /**
   * Whether Save also puts the rig in the cart.
   *
   * That is what lets Shopify's abandoned cart automation email a saved rig: it watches carts of
   * identified customers, not anything stored in a browser. So only a logged-in customer, and only
   * while the merchant keeps the section setting on.
   */
  #savesToCart() {
    return this.dataset.customerLoggedIn === 'true' && this.dataset.saveToCart === 'true';
  }

  #renderStage() {
    const stage = this.refs.stage;
    if (!(stage instanceof HTMLElement) || typeof stage.setLayers !== 'function') return;

    const layers = [];

    for (const optionId of this.#selectedOptionIds()) {
      const option = this.#options.get(optionId);
      if (!option) continue;
      if (this.#suppressedProducts.has(option.fields?.product)) continue;

      const display = option.display ?? {};
      const moduleHandle = this.#moduleHandleByOptionId.get(optionId) ?? '';

      if (this.#firstSelected(moduleHandle) !== optionId) {
        if (!display.wallImageUrl) continue;
        layers.push({
          id: `${optionId}:wall`,
          url: display.wallImageUrl,
          srcset: display.wallImageSrcset ?? '',
          order: Number(display.wallLayerOrder ?? WALL_LAYER_ORDER),
        });
        continue;
      }

      if (!display.previewImageUrl) continue;

      layers.push({
        id: option.id,
        url: display.previewImageUrl,
        srcset: display.previewImageSrcset ?? '',
        order: Number(display.layerOrder ?? 1),
      });
    }

    layers.sort((a, b) => a.order - b.order);

    stage.setLayers(layers);
  }

  #renderSummary(show = true) {
    const panel = this.refs.panel;
    if (!(panel instanceof HTMLElement)) return;

    const groups = [];
    const format = this.dataset.moneyFormat ?? '{{ amount }}';
    const lines = this.#selectedLines();

    for (const step of this.querySelectorAll('.sc-cfg-step')) {
      if (!(step instanceof HTMLElement)) continue;

      const stepHandle = step.dataset.step ?? '';
      const title = step.dataset.stepTitle ?? stepHandle;

      const rows = [];

      for (const line of lines) {
        if (this.#stepHandle(line.moduleHandle) !== stepHandle) continue;
        const quantity = line.quantity ?? 1;
        rows.push({
          name: quantity > 1 ? `${quantity} × ${line.title}` : line.title,
          price: formatMoney((Number(line.price) || 0) * quantity, format),
          thumbnail: line.thumbnail,
          note: line.autoAdded ? this.dataset.recommendedLabel : undefined,
        });
      }

      for (const row of step.querySelectorAll('.sc-cfg-sysrow[data-state="included"]')) {
        if (!(row instanceof HTMLElement)) continue;
        rows.push({
          name: row.querySelector('.sc-cfg-sysrow__title')?.textContent?.trim() ?? '',
          note: this.dataset.includedLabel,
          thumbnail: row.querySelector('img')?.getAttribute('src') ?? '',
        });
      }

      if (rows.length) groups.push({ title, rows });
    }

    const emptyText = this.dataset.emptyStepText ?? '';

    if (show) {
      panel.showSummary(groups, emptyText);
    } else {
      panel.renderSummary(groups, emptyText);
    }
  }

  #payload() {
    const qty = {};
    for (const [key, value] of this.#quantities) {
      if (value <= 1) continue;
      qty[this.#options.get(key)?.handle ?? key] = value;
    }

    return {
      v: 1,
      sel: this.#selectedOptionIds()
        .map((id) => this.#options.get(id)?.handle)
        .filter(Boolean),
      own: [...this.#skips],
      fb: [...this.#fallbackSelection],
      dis: Object.fromEntries(this.#dismissed),
      qty,
    };
  }

  #historyUrl(payload) {
    const url = new URL(window.location.href);
    for (const key of ['rig', 'own', 'qty', 'dis', 'fb']) url.searchParams.delete(key);
    if (payload.sel.length) url.searchParams.set('rig', payload.sel.join('.'));
    if (payload.own.length) url.searchParams.set('own', payload.own.join('.'));
    const qty = Object.entries(payload.qty)
      .map(([key, value]) => `${key}_${value}`)
      .join('.');
    if (qty) url.searchParams.set('qty', qty);
    const dis = Object.keys(payload.dis)
      .map((id) => id.split('/').pop())
      .join('.');
    if (dis) url.searchParams.set('dis', dis);
    if (payload.fb.length) url.searchParams.set('fb', payload.fb.join('.'));
    return url;
  }

  #persist(mode) {
    if (!mode) return;

    const payload = this.#payload();

    window.clearTimeout(this.#historyTimer);

    if (mode === 'replace') {
      window.history.replaceState(payload, '', this.#historyUrl(payload));
      return;
    }

    this.#historyTimer = window.setTimeout(() => {
      window.history.pushState(payload, '', this.#historyUrl(payload));
    }, 300);
  }

  #stored() {
    for (const name of ['sessionStorage', 'localStorage']) {
      try {
        const raw = window[name].getItem(SAVED_KEY);
        if (raw) return JSON.parse(raw);
      } catch {}
    }

    return null;
  }

  #store(payload) {
    const value = JSON.stringify(payload);
    let stored = false;

    for (const name of ['localStorage', 'sessionStorage']) {
      try {
        window[name].setItem(SAVED_KEY, value);
        stored = true;
      } catch {}
    }

    return stored;
  }

  /** Record this build's options so another market's build can map them (see PORTABLE_KEY). */
  #rememberPortable() {
    try {
      const known = JSON.parse(window.sessionStorage.getItem(PORTABLE_KEY) || '{}');
      for (const [handle, optionId] of this.#optionIdByHandle) {
        const option = this.#options.get(optionId);
        const role = this.#moduleHandleByOptionId.get(optionId);
        if (role) known[handle] = [role, portableName(option?.fields?.title ?? option?.display?.productTitle)];
      }
      window.sessionStorage.setItem(PORTABLE_KEY, JSON.stringify(known));
    } catch {}
  }

  /**
   * This build's option for a handle from the URL or a saved rig. A handle from another market's
   * build resolves to this build's copy of the same part: same step role, same name.
   */
  #optionIdFor(handle) {
    const own = this.#optionIdByHandle.get(handle);
    if (own) return own;

    let known;
    try {
      known = JSON.parse(window.sessionStorage.getItem(PORTABLE_KEY) || '{}')[handle];
    } catch {}
    if (!Array.isArray(known) || !known[1]) return undefined;

    const [role, name] = known;
    for (const [optionId, option] of this.#options) {
      if (this.#moduleHandleByOptionId.get(optionId) !== role) continue;
      if (portableName(option.fields?.title ?? option.display?.productTitle) === name) return optionId;
    }
    return undefined;
  }

  /**
   * Titles of the required steps that have nothing chosen and are not marked as already owned.
   *
   * "Rules inform, never force" covers the compatibility prompts; a required step is different.
   * A rig with no brake was reaching the cart, so Checkout now asks for one, and the step's own
   * "I already have one" box is always the way past it, including when every option is sold out.
   */
  #missingRequiredSteps() {
    const missing = [];

    for (const [moduleHandle, module] of this.#modulesByHandle) {
      if (module.fields?.required !== true || this.#systemModules.has(moduleHandle)) continue;
      if ((this.#selection.get(moduleHandle)?.size ?? 0) > 0) continue;
      if (this.#skips.has(moduleHandle)) continue;
      let fallback = false;
      for (const item of this.#fallbackItems.values()) {
        if (item.moduleHandle === moduleHandle && this.#fallbackSelection.has(item.variantId)) fallback = true;
      }
      if (fallback) continue;
      missing.push(module.fields?.title || moduleHandle);
    }

    return missing;
  }

  /** Start the build again from nothing. Back in the browser returns to the build just cleared. */
  #reset() {
    this.#applyPayload({});
    this.#clearStore();
    this.#saved = false;
    this.#update();
  }

  /**
   * The remembered rig as `{ key, price, rig }`, or null. `rig` is the composition that was added
   * (see `#rigFingerprint`), so an unchanged rig can be recognised without another add. A value
   * stored by an older build is a bare key.
   */
  #storedCartLine() {
    for (const name of ['sessionStorage', 'localStorage']) {
      try {
        const raw = window[name].getItem(CART_LINE_KEY);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed.key === 'string') {
            return { key: parsed.key, price: parsed.price ?? null, rig: parsed.rig ?? null };
          }
        } catch {}
        return { key: raw, price: null, rig: null };
      } catch {}
    }

    return null;
  }

  #storeCartLine(line) {
    for (const name of ['localStorage', 'sessionStorage']) {
      try {
        if (line?.key) {
          const value = { key: line.key, price: line.price ?? null, rig: line.rig ?? null };
          window[name].setItem(CART_LINE_KEY, JSON.stringify(value));
        } else {
          window[name].removeItem(CART_LINE_KEY);
        }
      } catch {}
    }
  }

  /** The rig as one string: its variants and quantities, in a fixed order, so two builds compare. */
  #rigFingerprint(items) {
    return items
      .map((item) => `${item.id}x${item.quantity}`)
      .sort()
      .join(',');
  }

  /**
   * The cart's lines in cart order (newest first), each as `{ key, price, quantity }`, or null
   * when the cart will not say. Read before and after the add so the new rig is identified by
   * what actually appeared rather than by what `cart/add.js` reports.
   */
  async #cartLines() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return null;
      const body = await response.json();
      if (!Array.isArray(body?.items)) return null;
      return body.items.map((item) => ({
        key: item.key,
        price: item.original_line_price ?? null,
        quantity: item.quantity ?? 0,
      }));
    } catch {
      return null;
    }
  }

  #clearStore() {
    for (const name of ['localStorage', 'sessionStorage']) {
      try {
        window[name].removeItem(SAVED_KEY);
      } catch {}
    }
  }

  #payloadFromParams(params) {
    const split = (key) => (params.get(key) ?? '').split('.').filter(Boolean);

    const qty = {};
    for (const pair of split('qty')) {
      const at = pair.lastIndexOf('_');
      const key = pair.slice(0, at);
      const value = Number(pair.slice(at + 1));
      if (key && value > 1) qty[key] = value;
    }

    const dis = {};
    for (const id of split('dis')) {
      if (/^\d+$/.test(id)) dis[`gid://shopify/Product/${id}`] = '';
    }

    return { v: 1, sel: split('rig'), own: split('own'), fb: split('fb'), dis, qty };
  }

  #restore() {
    const params = new URLSearchParams(window.location.search);

    if (['rig', 'own', 'qty', 'dis', 'fb'].some((key) => params.has(key))) {
      this.#applyPayload(this.#payloadFromParams(params));
      return false;
    }

    const stored = this.#stored();
    if (!stored) return false;
    this.#applyPayload(stored);
    return true;
  }

  #applyPayload(payload) {
    this.#selection.clear();
    this.#selectionSeq.clear();
    this.#skips.clear();
    this.#autoAdded.clear();
    this.#dismissed.clear();
    this.#fallbackSelection.clear();
    this.#quantities.clear();

    for (const [key, value] of Object.entries(payload?.qty ?? {})) {
      const quantity = Math.max(1, Number(value) || 1);
      const optionId = this.#optionIdFor(key);
      if (optionId) this.#quantities.set(optionId, quantity);
      else if (this.#fallbackItems.has(key)) this.#quantities.set(key, quantity);
    }

    for (const handle of payload?.sel ?? []) {
      const optionId = this.#optionIdFor(handle);
      if (optionId) this.#addSelection(optionId);
    }
    // A restored hub is indistinguishable from one the builder added, so treat it as one: it then
    // leaves with the part that asked for it, as it would have before the reload.
    this.#adoptSystemSelections = true;

    for (const moduleHandle of payload?.own ?? []) {
      if (this.#modulesByHandle.has(moduleHandle)) this.#skips.add(moduleHandle);
    }

    for (const variantId of payload?.fb ?? []) {
      // A shared or bookmarked rig can outlive the stock it was built from, so restore drops
      // anything that has since sold out rather than reviving an unbuyable line.
      if (this.#fallbackItems.get(variantId)?.available !== false) {
        if (this.#fallbackItems.has(variantId)) this.#fallbackSelection.add(variantId);
      }
    }

    const signature = this.#triggerSignature();
    for (const productId of Object.keys(payload?.dis ?? {})) {
      this.#dismissed.set(productId, signature);
    }

    for (const input of this.querySelectorAll('[data-skip-step]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.checked = this.#skips.has(input.dataset.skipStep ?? '');
    }
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const accSummary = event.target.closest('.sc-cfg-acc-group__summary');
    if (accSummary instanceof HTMLElement) {
      event.preventDefault();
      const details = accSummary.closest('details');
      const body = details?.querySelector('.sc-cfg-acc-group__body');
      if (details instanceof HTMLDetailsElement && body instanceof HTMLElement) {
        this.#toggleAccordion(details, body);
      }
      return;
    }

    const pill = event.target.closest('[data-filter-collection]');
    if (pill instanceof HTMLElement) {
      const step = pill.closest('.sc-cfg-step');
      if (step instanceof HTMLElement) {
        for (const button of step.querySelectorAll('.sc-cfg-step__filter')) {
          button.setAttribute('aria-pressed', String(button === pill));
        }
        const handle = pill.dataset.filterCollection ?? '';
        for (const card of step.querySelectorAll('.sc-cfg-card')) {
          if (!(card instanceof HTMLElement)) continue;
          card.hidden = handle !== '' && !(card.dataset.collections ?? '').split(' ').includes(handle);
        }
        this.#refreshRails(step);
      }
      return;
    }

    const compare = event.target.closest('[data-compare-step]');
    if (compare instanceof HTMLElement && compare.dataset.compareStep) {
      this.refs.panel.showCompare(compare.dataset.compareStep);
      return;
    }

    if (event.target.closest('[data-open-summary]')) {
      const panel = this.refs.panel;
      if (panel instanceof HTMLElement && panel.isOpen && panel.dataset.view === 'summary') {
        panel.showSteps();
      } else {
        this.#renderSummary(true);
      }
      return;
    }

    if (event.target.closest('[data-save]')) {
      this.#toggleSave();
      return;
    }

    if (event.target.closest('[data-reset]')) {
      this.#reset();
      return;
    }

    if (event.target.closest('[data-checkout]')) {
      this.#checkout();
      return;
    }

    const specsToggle = event.target.closest('[data-toggle-specs]');
    if (specsToggle instanceof HTMLElement) {
      const card = specsToggle.closest('.sc-cfg-card');
      if (card instanceof HTMLElement) {
        const willOpen = card.dataset.specsOpen !== 'true';
        card.style.minHeight = willOpen ? `${card.offsetHeight}px` : '';
        card.dataset.specsOpen = String(willOpen);
        specsToggle.setAttribute('aria-expanded', String(willOpen));
      }
      return;
    }

    const qtyButton = event.target.closest('[data-qty-plus], [data-qty-minus]');
    if (qtyButton instanceof HTMLElement) {
      const card = qtyButton.closest('[data-option-id], [data-fallback-id]');
      if (!(card instanceof HTMLElement)) return;

      const key = card.dataset.optionId || card.dataset.fallbackId || '';
      if (!key) return;

      const delta = qtyButton.hasAttribute('data-qty-plus') ? 1 : -1;
      const next = Math.max(1, this.#qtyOf(key) + delta);
      if (next === this.#qtyOf(key)) return;

      this.#quantities.set(key, next);

      const selected = card.dataset.fallbackId
        ? this.#fallbackSelection.has(card.dataset.fallbackId)
        : this.#isSelected(key);

      if (selected) {
        this.#update();
      } else {
        card.dataset.qty = String(next);
        const value = card.querySelector('[data-qty-value]');
        if (value) value.textContent = String(next);
        this.#persist(true);
      }
      return;
    }

    const notifyButton = event.target.closest('[data-notify-option]');
    if (notifyButton instanceof HTMLElement) {
      this.#openNotifyForm(notifyButton);
      return;
    }

    const trigger = event.target.closest('[data-select-option]');
    if (!(trigger instanceof HTMLElement)) return;

    const host = trigger.closest('[data-option-id], [data-fallback-id]');
    if (!(host instanceof HTMLElement)) return;

    if (host.dataset.fallbackId) {
      const variantId = host.dataset.fallbackId;
      if (this.#fallbackSelection.has(variantId)) this.#fallbackSelection.delete(variantId);
      else if (this.#fallbackItems.get(variantId)?.available !== false) {
        this.#fallbackSelection.add(variantId);
      }
      this.#update();
      return;
    }

    const optionId = host.dataset.optionId ?? trigger.dataset.optionId ?? '';
    if (!optionId) return;

    if (this.#isSelected(optionId)) {
      this.#removeSelection(optionId);
      this.#dismissAuto(optionId);
    } else {
      this.#addSelection(optionId);
    }

    this.#update();
  };

  #toggleAccordion(details, body) {
    const opening = !details.open;

    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      details.open = opening;
      return;
    }

    const running = this.#accordionAnimations.get(body);
    const from = running ? body.getBoundingClientRect().height : opening ? 0 : body.scrollHeight;
    const fromOpacity = running ? Number(getComputedStyle(body).opacity) : opening ? 0 : 1;
    const fromPad = running || !opening ? getComputedStyle(body).paddingBlockEnd : '0px';

    running?.cancel();

    details.open = true;
    details.toggleAttribute('data-collapsing', !opening);

    const toPad = getComputedStyle(body).paddingBlockEnd;
    const to = opening ? body.scrollHeight : 0;

    const animation = body.animate(
      {
        blockSize: [`${from}px`, `${to}px`],
        paddingBlockEnd: [fromPad, opening ? toPad : '0px'],
        opacity: [fromOpacity, opening ? 1 : 0],
      },
      {
        duration: opening ? ACCORDION_OPEN_DURATION : ACCORDION_CLOSE_DURATION,
        easing: ACCORDION_EASING,
        fill: 'forwards',
      },
    );

    this.#accordionAnimations.set(body, animation);

    animation.finished
      .then(() => {
        this.#accordionAnimations.delete(body);
        details.open = opening;
        details.removeAttribute('data-collapsing');
        animation.cancel();
      })
      .catch(() => {});
  }

  #dismissAuto(optionId) {
    if (!this.#autoAdded.has(optionId)) return;
    const option = this.#options.get(optionId);
    this.#autoAdded.delete(optionId);
    if (option?.fields?.product) this.#dismissed.set(option.fields.product, this.#triggerSignature());
  }

  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.skipStep) return;

    const stepHandle = input.dataset.skipStep;

    for (const moduleHandle of this.#modulesByHandle.keys()) {
      if (this.#stepHandle(moduleHandle) !== stepHandle) continue;

      if (input.checked) {
        this.#skips.add(moduleHandle);
        const selected = this.#selection.get(moduleHandle);
        if (selected) {
          for (const optionId of selected) this.#autoAdded.delete(optionId);
          selected.clear();
        }
      } else {
        this.#skips.delete(moduleHandle);
      }
    }

    this.#update();
  };

  #preloadStep(handle) {
    if (!handle) return;

    const stage = this.refs.stage;
    if (!(stage instanceof HTMLElement) || typeof stage.preload !== 'function') return;

    const entries = [];
    for (const [optionId, moduleHandle] of this.#moduleHandleByOptionId) {
      if (this.#stepHandle(moduleHandle) !== handle) continue;
      const display = this.#options.get(optionId)?.display;
      if (!display) continue;
      if (display.previewImageUrl) {
        entries.push({ url: display.previewImageUrl, srcset: display.previewImageSrcset ?? '' });
      }
      if (display.wallImageUrl) {
        entries.push({ url: display.wallImageUrl, srcset: display.wallImageSrcset ?? '' });
      }
    }

    stage.preload(entries);
  }

  #onStepChange = (event) => {
    this.#preloadStep(event.detail?.handle);
  };

  #onPopState = (event) => {
    if (!this.#booted) return;

    if (event.state?.v === 1) {
      this.#applyPayload(event.state);
    } else {
      this.#applyPayload(this.#payloadFromParams(new URLSearchParams(window.location.search)));
    }

    this.#update({ history: false });
  };

  #toggleSave() {
    if (this.#saved) {
      this.#clearStore();
      this.#saved = false;
      this.#renderSaveButton();
      return;
    }

    const payload = this.#payload();

    if (!this.#store(payload)) return;

    this.#saved = true;

    window.clearTimeout(this.#historyTimer);
    window.history.replaceState(payload, '', this.#historyUrl(payload));

    this.#renderSaveButton();

    // A logged-in customer's save also lands in their cart, replacing any rig this browser put
    // there before, so the abandoned cart email carries exactly the build they saved. The customer
    // stays on the builder; a failure shows in the same strip a failed checkout would.
    // An unfinished rig is still saved here, but only a complete one goes to the cart.
    if (this.#savesToCart() && this.#missingRequiredSteps().length === 0) void this.#addRigToCart(this.refs.saveButton);

    if (this.dataset.customerLoggedIn === 'true' && this.dataset.emailSavedRig === 'true') void this.#emailSavedRig(payload);
  }

  /**
   * Ask the app to fire its "Rig saved" Flow trigger, which the merchant turns into an email.
   *
   * Goes through the app proxy, which Shopify signs with the logged-in customer's ID, so the email
   * can only reach the customer who clicked Save. Best effort: a failure changes nothing on the
   * page, the rig is still saved. The same rig is sent once, however often Save is clicked.
   */
  async #emailSavedRig(payload) {
    const lines = this.#selectedLines();
    if (!lines.length) return;

    const link = this.#historyUrl(payload).toString();
    const summary = lines
      .map((line) => {
        const role = this.#stepTitle(line.moduleHandle);
        const quantity = (line.quantity ?? 1) > 1 ? `${line.quantity} × ` : '';
        return `${role ? `${role}: ` : ''}${quantity}${line.title}`;
      })
      .join('; ');

    const fingerprint = `${link}|${summary}`;
    try {
      if (window.localStorage.getItem(EMAILED_KEY) === fingerprint) return;
    } catch {}

    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}apps/simucube/rig-saved`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          link,
          summary,
          total: this.refs.total?.textContent?.trim() ?? '',
          market: this.dataset.marketName ?? '',
        }),
      });
      if (response.ok) {
        try {
          window.localStorage.setItem(EMAILED_KEY, fingerprint);
        } catch {}
      }
    } catch {
      /* the rig is saved either way; only the email is missed */
    }
  }

  /**
   * The one image that stands for the whole rig outside the Rig Builder: the checkout thumbnail and
   * the order emails, which can only show a single picture for the merged "Custom Rig" line.
   *
   * The wheelbase's product photo, not its stage render. The render is a transparent PNG the size of
   * the whole stage with the wheelbase in one corner, so it can line up with the other layers; on
   * its own at checkout it shrank to a speck (from 8 Sept until this change). The cart page is not
   * affected: it rebuilds the rig from every component's own layer (_rig_layer).
   *
   * Liquid's `image_url` returns a protocol-relative URL, which the Cart Transform's `URL` scalar
   * rejects, so give it a scheme before it ever leaves here.
   */
  #bundleImage(lines) {
    const wheelbase = lines.find((line) => line.moduleHandle === 'wheelbase');
    const url =
      wheelbase?.thumbnail ||
      lines.find((line) => line.thumbnail)?.thumbnail ||
      wheelbase?.rigRender ||
      lines.find((line) => line.rigRender)?.rigRender ||
      '';
    return url.startsWith('//') ? `https:${url}` : url;
  }

  /**
   * The rig as one picture, stored in the shop's Files, or '' when it cannot be made in time.
   *
   * The cart page draws a rig by stacking the backdrop and each part's transparent render, but
   * checkout and the order emails can show only one image. This draws exactly that stack, in the
   * cart's square frame (backdrop covering, parts contained and centred, in layer order), and asks
   * the app to store it (app/routes/proxy.rig-image.tsx), which reuses the file for an identical
   * build. The renders come from this shop's own CDN path, so the canvas stays exportable.
   */
  async #rigPicture(items, baseUrl) {
    const layers = items
      .map((item, index) => ({ src: item.properties._rig_layer, order: Number(item.properties._rig_layer_order ?? 0), index }))
      .filter((layer) => layer.src)
      .sort((a, b) => a.order - b.order || a.index - b.index);
    if (!layers.length) return '';

    const sharp = (url) => {
      const absolute = url.startsWith('//') ? `https:${url}` : url;
      return /[?&]width=\d+/.test(absolute) ? absolute.replace(/([?&])width=\d+/, '$1width=800') : absolute;
    };
    const load = (url) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = sharp(url);
      });

    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 9000);
    try {
      const [base, ...parts] = await Promise.all([baseUrl ? load(baseUrl).catch(() => null) : null, ...layers.map((l) => load(l.src))]);

      const size = 800;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d');
      if (!context) return '';
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, size, size);

      const draw = (img, fit) => {
        const scale = (fit === 'cover' ? Math.max : Math.min)(size / img.naturalWidth, size / img.naturalHeight);
        const width = img.naturalWidth * scale;
        const height = img.naturalHeight * scale;
        context.drawImage(img, (size - width) / 2, (size - height) / 2, width, height);
      };
      if (base) draw(base, 'cover');
      for (const part of parts) draw(part, 'contain');

      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}apps/simucube/rig-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ image: canvas.toDataURL('image/jpeg', 0.85) }),
        signal: controller.signal,
      });
      if (!response.ok) return '';
      const body = await response.json();
      return typeof body?.url === 'string' && body.url.startsWith('https://') ? body.url : '';
    } catch {
      return '';
    } finally {
      window.clearTimeout(timer);
    }
  }

  /** Show a cart failure, preferring the specific reason over the generic retry copy. */
  #showCartError(reason) {
    const error = this.refs.error;
    if (!(error instanceof HTMLElement)) return;
    error.hidden = false;
    error.textContent = reason || this.dataset.cartErrorText || '';
  }

  async #checkout() {
    const missing = this.#missingRequiredSteps();
    if (missing.length) {
      const template = this.dataset.cartIncompleteText || 'Choose your [steps] first, or tick "I already have" on that step.';
      this.#showCartError(template.replaceAll('[steps]', missing.join(', ')));
      return;
    }

    const button = this.refs.checkoutButton;
    button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;

    if (await this.#addRigToCart(button)) {
      if (button) button.disabled = true;
      window.location.assign(Theme.routes.cart_url);
      return;
    }

    button?.removeAttribute('aria-busy');
  }

  /**
   * Put the current rig in the cart in place of the one this browser added before.
   *
   * Shared by Checkout, which then leaves for the cart, and by Save for a logged-in customer,
   * which stays put. `button` is disabled for the duration and re-enabled on failure; on success
   * it is left disabled only when the caller is about to navigate away, so it is re-enabled here
   * too and Checkout simply never gets to see that.
   */
  async #addRigToCart(button) {
    const lines = this.#selectedLines().filter((line) => line.variantId);
    if (!lines.length) return false;

    const error = this.refs.error;
    if (error instanceof HTMLElement) error.hidden = true;

    // Catch a sold-out part before the round trip. A rig restored from a shared link or a saved
    // session can outlive the stock it was built from, and Shopify's own rejection names a variant
    // id, which means nothing to the customer. Name the part instead.
    // A line with no variant can never be bought. Liquid yields nil for a draft or deleted
    // product, and posting a null id fails the WHOLE rig with "Required parameter missing or
    // invalid: id" rather than naming the part, so catch it here too and not only in the bootstrap.
    const unavailable = lines.filter((line) =>
      !line.variantId || (line.optionId ? !this.#isAvailable(line.optionId) : line.available === false),
    );
    if (unavailable.length) {
      const names = unavailable.map((line) => line.title).filter(Boolean);
      const template = this.dataset.cartUnavailableText || 'No longer available: [parts]';
      this.#showCartError(template.replaceAll('[parts]', names.join(', ')));
      return false;
    }

    if (button) button.disabled = true;

    const bundleId = crypto.randomUUID();
    // The Cart Transform cannot read product images (they are not in its input schema), and the
    // bundle parent product has none, so the merged cart line would show no thumbnail at all.
    // Send the wheelbase thumbnail with the rig and let the function pass it through, so the cart
    // shows the build the customer actually configured.
    const bundleImage = this.#bundleImage(lines);
    const stageBase = this.dataset.stageBaseThumb ?? '';
    const items = lines.map((line) => ({
      id: Number(line.variantId),
      quantity: line.quantity ?? 1,
      properties: {
        _bundle_id: bundleId,
        _bundle_title: this.dataset.bundleTitle ?? '',
        _configurator: 'rig-builder',
        ...(bundleImage ? { _bundle_image: bundleImage } : {}),
        // Each component carries its own stage layer so the cart can rebuild the whole rig rather
        // than showing one part of it. The merged parent hides its properties, but Liquid can read
        // a component's, which is the only route back to the composition.
        ...(line.layer ? { _rig_layer: line.layer, _rig_layer_order: String(line.layerOrder) } : {}),
        // The backdrop the rig is composed against. It is a section setting rather than an option,
        // so it has to travel separately or the cart shows parts floating on nothing.
        ...(stageBase ? { _rig_base: stageBase } : {}),
        ...(line.autoAdded ? { _auto_added: '1' } : {}),
        // Which job the part does. The same ActivePedal can be the brake, the throttle or the
        // clutch, and neither the cart nor the order said which. Underscored so checkout does not
        // print it twice; the cart renders it, and the order in the admin shows it for picking.
        ...(this.#stepTitle(line.moduleHandle) ? { _rig_role: this.#stepTitle(line.moduleHandle) } : {}),
      },
    }));

    // The rig this browser already put in the cart, if any. Captured before the add so a failed
    // add leaves the existing rig untouched.
    const previous = this.#storedCartLine();
    const before = await this.#cartLines();

    // Save followed by Checkout, or Checkout twice, offers the cart a rig it already holds. Adding
    // it again would only merge into that line at quantity two, and Shopify will not let the
    // quantity be set back (see replacementPlan), so an unchanged rig that is still in the cart is
    // left exactly as it is. Only the + button in the cart is meant to make two of it.
    const rig = this.#rigFingerprint(items);
    const unchanged =
      previous?.rig === rig &&
      Array.isArray(before) &&
      before.some((line) => line.key === previous.key && line.price === previous.price);
    if (unchanged) {
      if (button) button.disabled = false;
      return true;
    }

    // Checkout and the order emails show one picture for the merged rig. Draw the same stack the
    // cart page shows and send that; the wheelbase photo above stays if drawing or storing fails.
    const picture = await this.#rigPicture(items, stageBase);
    if (picture) for (const item of items) item.properties._bundle_image = picture;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        // Shopify explains itself in the body ("All 3 Simucube 3 Ultimate are in your cart",
        // "sold out", a stock ceiling). Discarding it left the customer, and us, with nothing but
        // "please try again" and no way to tell a stock problem from an outage.
        let reason = '';
        try {
          const body = await response.json();
          reason = body?.description || body?.message || '';
        } catch {
          /* a non-JSON error body tells us nothing extra; fall back to the generic copy */
        }
        this.#showCartError(reason);
        if (button) button.disabled = false;
        return false;
      }

      // Replace rather than append: remember the rig that just landed and take the previous one
      // out, by line number (see replacementPlan). Done in this order on purpose: if the removal
      // fails the customer has a duplicate to delete, which beats losing the rig they just built.
      const after = before ? await this.#cartLines() : null;
      const plan = replacementPlan(previous, before, after);

      if (plan.remember) {
        this.#storeCartLine({ ...plan.remember, rig });
      } else {
        // The cart would not describe itself; fall back to what the response says and forget the
        // old rig, since removing by key could hit the wrong line.
        let addedKey = null;
        try {
          const body = await response.json();
          addedKey = body?.items?.[0]?.key ?? null;
        } catch {
          /* a cart that will not describe itself still added the rig; just cannot be replaced later */
        }
        this.#storeCartLine(addedKey ? { key: addedKey, price: null, rig } : null);
      }

      if (plan.change) {
        try {
          await fetch(`${Theme.routes.cart_change_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(plan.change),
          });
        } catch {
          /* the old rig stays in the cart; the customer can remove it, nothing is lost */
        }
      }

      if (button) button.disabled = false;
      return true;
    } catch {
      this.#showCartError('');
      if (button) button.disabled = false;
      return false;
    }
  }
}

if (!customElements.get('sc-configurator')) {
  customElements.define('sc-configurator', SimucubeConfigurator);
}
